import { ButtonHTMLAttributes, forwardRef, useEffect } from 'react'

import { useChatToggle } from '@/src/hooks'
import { useLayoutContext } from '@/src/context'

import { useAppDispatch, useAppSelector } from '@/src/store/hooks'
import { selectBranding } from '@/src/store/slices/meetData'
import { selectChatState, selectUnreadMessages, setIsOpenChat } from '@/src/store/slices/chat'
import { setIsOpenMedia } from '@/src/store/slices/media'

/** @public */
export interface ChatToggleProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  closeBar?: boolean
}

/**
 * The `ChatToggle` component is a button that toggles the visibility of the `Chat` component.
 * @remarks
 * For the component to have any effect it has to live inside a `LayoutContext` context.
 *
 * @example
 * ```tsx
 * <LiveKitRoom>
 *   <ToggleChat />
 * </LiveKitRoom>
 * ```
 * @public
 */
export const ChatToggle = forwardRef<HTMLButtonElement, ChatToggleProps>(function ChatToggle(
  { closeBar, ...otherProps }: ChatToggleProps,
  ref,
) {
  const dispatch = useAppDispatch()
  const { dispatch: layoutDispatch, state } = useLayoutContext().widget
  const { isOpen } = useAppSelector(selectChatState)
  const unreadMessages = useAppSelector(selectUnreadMessages)
  // const { mergedProps, unreadMessages } = useChatToggle({ props: otherProps })
  const { primary_color } = useAppSelector(selectBranding)

  const handleClick = () => {
    if (!isOpen) {
      layoutDispatch?.({ msg: 'hide_participants' })
      dispatch(setIsOpenMedia(false))
    }

    dispatch(setIsOpenChat(!isOpen))
  }

  useEffect(() => {
    if (state?.showParticipants) {
      dispatch(setIsOpenChat(false))
    }
  }, [state?.showParticipants])

  return (
    <div style={{ display: 'flex', position: closeBar ? 'static' : 'relative' }}>
      {unreadMessages !== 0 && !closeBar && (
        <span className="lk-counter" style={{ background: `${primary_color}` }}>
          {unreadMessages ? (unreadMessages < 10 ? unreadMessages.toFixed(0) : '9+') : '1'}
        </span>
      )}

      <button
        ref={ref}
        onClick={handleClick}
        className="lk-button chat-toggle"
        aria-pressed={isOpen && !closeBar}
      >
        {otherProps.children}
      </button>
    </div>
  )
})
